'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

const testimonials = [
  {
    id: 1,
    quote: "ADV was great to work with. They made the process easy and, most importantly, profitable. They know what they're doing and you can see it in the results",
    name: "Mark Davis",
    company: "Vector Solutions",
    image: "/images/Mark Davis image.png"
  },
  {
    id: 2,
    quote: "Great agency with clear methods and very open-book about everything that they do. Will work with them on our next campaign.",
    name: "Daniel Kim",
    company: "Lee Advisory",
    image: "/images/Daniel Kim image.png"
  },
  {
    id: 3,
    quote: "The team at ADV Online moved fast to solve our business marketing pain points. They gave great, actionable advice and then implemented it. Always responsive, always transparent, always effective.",
    name: "Elena Rodriguez",
    company: "UrbanBox",
    image: "/images/Elena Rodriguez image.png"
  },
  {
    id: 4,
    quote: "Within 30 days of setting up an account with ADV, our adwords campaign spend dropped by half even as our conversions went up significantly.",
    name: "Michael Zhang",
    company: "TechPulse",
    image: "/images/Michael Zhang image.png"
  },
  {
    id: 5,
    quote: "We've worked with ADV for almost a year. They are total PPC nerds, which means they have a wealth of actionable knowledge. We are super happy with every aspect of their work and their client communications. They're never too busy to answer questions or client anxieties.",
    name: "Sarah Chen",
    company: "Aura Digital",
    image: "/images/Sarah Chen image.png"
  }
];

export default function TestimonialsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  
  useEffect(() => {
    const updateSlidesToShow = () => {
      const newSlidesToShow = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
      setSlidesToShow(newSlidesToShow);
      const newMaxIndex = Math.max(0, testimonials.length - newSlidesToShow);
      setCurrentIndex(prev => Math.min(prev, newMaxIndex));
    };
    
    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);
  
  const maxIndex = Math.max(0, testimonials.length - slidesToShow);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? prev : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? prev : prev - 1));
  };

  const getCardWidth = () => {
    if (slidesToShow === 1) return '100%';
    if (slidesToShow === 2) return 'calc((100% - 20px) / 2)';
    return 'calc((100% - 40px) / 3)';
  };

  return (
    <div className="flex flex-col gap-6 sm:gap-8 lg:gap-[40px] items-center w-full">
      <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
        <button 
          onClick={prevSlide}
          disabled={currentIndex <= 0}
          className="w-8 h-8 sm:w-10 sm:h-10 lg:w-[50px] lg:h-[50px] relative flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity disabled:cursor-not-allowed"
        >
          <Image 
            src="/icons/arrow_right.svg" 
            alt="Previous" 
            width={50} 
            height={50} 
            className="rotate-180 w-full h-full"
            style={currentIndex <= 0 ? { 
              filter: 'brightness(0) saturate(100%) invert(79%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(98%) contrast(95%)',
              opacity: 1
            } : {}}
          />
        </button>
        
        <div className="relative overflow-hidden flex-1" style={{ paddingRight: slidesToShow > 1 ? '18px' : '0px' }}>
          <div 
            className="flex transition-transform duration-300 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / slidesToShow)}%)`,
            }}
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-[12px] px-6 sm:px-8 lg:px-[40px] py-12 sm:py-16 lg:py-[100px] flex-shrink-0 flex flex-col justify-between min-h-[350px] sm:h-[400px] lg:h-[440px]"
                style={{
                  width: getCardWidth(),
                  marginRight: index < testimonials.length - 1 && slidesToShow > 1 ? '20px' : '0px'
                }}
              >
                <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                  &quot;{testimonial.quote}&quot;
                </p>
                <div className="flex gap-[15px] items-end mt-auto">
                  <div className="w-[80px] h-[80px] rounded-full relative shrink-0">
                    <Image 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      width={80} 
                      height={80} 
                      className="rounded-full object-cover" 
                    />
                  </div>
                  <div className="flex flex-col gap-[5px]">
                    <div className="font-manrope font-normal text-[15px] text-black">{testimonial.name}</div>
                    <div className="font-manrope font-normal text-[13px] text-black">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <button 
          onClick={nextSlide}
          disabled={currentIndex >= maxIndex}
          className="w-8 h-8 sm:w-10 sm:h-10 lg:w-[50px] lg:h-[50px] relative flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity disabled:cursor-not-allowed"
        >
          <Image 
            src="/icons/arrow_right.svg" 
            alt="Next" 
            width={50} 
            height={50} 
            className="w-full h-full"
            style={currentIndex >= maxIndex ? { 
              filter: 'brightness(0) saturate(100%) invert(79%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(98%) contrast(95%)',
              opacity: 1
            } : {}}
          />
        </button>
      </div>
      
      <div className="flex gap-2 sm:gap-[7px] items-center">
        {[...Array(testimonials.length)].map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 sm:w-3 sm:h-3 lg:w-[12px] lg:h-[12px] rounded-full transition-colors ${
              index === currentIndex ? 'bg-[#cacaca]' : 'bg-[#6b7280]'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
